import React, { useState, useEffect } from 'react';
import clsx from 'clsx';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import Accordion from 'components/Accordion';
import APModal from 'components/Modal/APModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useFloatingTooltip } from 'hooks/useFloatingTooltip';
import { MAX_NOTE_NAME_LENGTH as maxTitleLength } from 'utils/constants';
import { useAutosizeTextarea } from 'hooks/useAutosizeTextarea';
import NoteTemplateSortable from './NoteTemplateSortable';
import './note-templates.scss';

const NoteTemplateTitle = ({ title, updateTitle }) => {
  const [editTitle, setEditTitle] = useState(false);
  const [accordionTitle, setAccordionTitle] = useState(title);

  const onChangeTitle = (e) => {
    const { value } = e.target;
    if (value !== '') {
      updateTitle({ title: value });
      setAccordionTitle(value);
    }
    setEditTitle(false);
  };

  return (
    <>
      {editTitle ? (
        <AutosizeTextarea
          defaultValue={
            accordionTitle.length <= maxTitleLength || !maxTitleLength
              ? accordionTitle
              : `${accordionTitle.slice(0, maxTitleLength)}`
          }
          onBlur={onChangeTitle}
          autoFocus
          onClick={(e) => e.stopPropagation()}
          onFocus={(e) => {
            e.target.selectionStart = e.target.value.length;
          }}
        />
      ) : (
        <span
          onClick={(e) => {
            setEditTitle(true);
            e.stopPropagation();
          }}
        >
          {accordionTitle.length <= maxTitleLength || !maxTitleLength
            ? accordionTitle
            : `${accordionTitle.slice(0, maxTitleLength)}...`}
        </span>
      )}
    </>
  );
};

const AutosizeTextarea = (props) => {
  const { textareaRef } = useAutosizeTextarea({ enterToSubmit: true });

  return (
    <textarea
      ref={textareaRef}
      maxLength={maxTitleLength}
      className="note-template-folder__title--edit unstyled"
      {...props}
    />
  );
};

const NoteTemplateGroup = ({
  id,
  groupIndex,
  name,
  order,
  expand = true,
  templates,
  isLast = false,
  isDragging = false,
  updateNoteTemplateFolder,
  deleteNoteTemplateFolder,
  reorderNoteTemplateFolders,
  ableToDelete,
}) => {
  const { isOver, node, setNodeRef } = useDroppable({ id });

  const [deleteConfirmOpened, setDeleteConfirmOpened] = useState(false);
  const [hasTemplates, setHasTemplates] = useState(false);
  const [groupHeight, setGroupHeight] = useState(node?.current?.offsetHeight);
  const [maintainHeight, setMaintainHeight] = useState(false);
  const [tooltipMessage, setTooltipMessage] = useState('');

  const {
    showTooltip,
    hideTooltip,
    updateTooltip,
    tooltipElement,
  } = useFloatingTooltip(tooltipMessage);

  const deleteGroup = () => {
    deleteNoteTemplateFolder({ id });
  };

  const updateTitle = ({ title }) => {
    updateNoteTemplateFolder({ id, title });
  };

  const reorderGroups = (newOrder) => {
    reorderNoteTemplateFolders({ id, position: newOrder });
  };

  useEffect(() => {
    if (templates.length > 0) {
      setHasTemplates(true);
      setMaintainHeight(false);
    } else if (isDragging && node?.current) {
      setMaintainHeight(true);
    } else {
      setHasTemplates(false);
      setGroupHeight(node.current.offsetHeight);
      setMaintainHeight(false);
    }
  }, [templates.length, isDragging]);

  useEffect(() => {
    if (node.current) setGroupHeight(node.current.offsetHeight);
  }, [node, expand]);

  const style = {
    border: isOver ? '1px dashed grey' : '1px solid transparent',
    height: maintainHeight ? `${groupHeight}px` : undefined,
  };

  return (
    <>
      {tooltipElement}
      <SortableContext
        items={templates}
        strategy={verticalListSortingStrategy}
      >
        <div
          className={clsx(
            'note-group',
            { 'open': hasTemplates && expand },
            groupIndex === 0 ? 'first' : null,
            isLast ? 'last' : null,
          )}
          ref={setNodeRef}
        >
          <div className="note-template-folder">
            <Accordion
              open
              title={(
                <NoteTemplateTitle
                  title={name}
                  updateTitle={updateTitle}
                />
              )}
              className="settings-accordion"
              style={style}
            >
              {templates?.map((item, index) => (
                <NoteTemplateSortable
                  key={`custom-${item.id}`}
                  item={item}
                  index={index}
                  isSortable
                />
              ))}
            </Accordion>
            <div className="note-template__actions">
              <i
                className="note-template__icon move-down"
                onClick={() => reorderGroups(order + 1)}
                onMouseEnter={e => {
                  showTooltip();
                  updateTooltip(e);
                  setTooltipMessage('Move folder down');
                }}
                onMouseMove={e => updateTooltip(e)}
                onMouseLeave={hideTooltip}
              >
                <FontAwesomeIcon icon={['fal', 'long-arrow-down']} />
              </i>
              <i
                className="note-template__icon move-up"
                onClick={() => reorderGroups(order - 1)}
                onMouseEnter={e => {
                  showTooltip();
                  updateTooltip(e);
                  setTooltipMessage('Move folder up');
                }}
                onMouseMove={e => updateTooltip(e)}
                onMouseLeave={hideTooltip}
              >
                <FontAwesomeIcon icon={['fal', 'long-arrow-up']} />
              </i>
              {!hasTemplates && ableToDelete ? (
                <i
                  className="note-template__icon delete"
                  onClick={() => setDeleteConfirmOpened(true)}
                  onMouseEnter={e => {
                    showTooltip();
                    updateTooltip(e);
                    setTooltipMessage('Delete folder');
                  }}
                  onMouseMove={e => updateTooltip(e)}
                  onMouseLeave={hideTooltip}
                >
                  <FontAwesomeIcon icon={['fal', 'times-circle']} />
                </i>
              ) : null}
            </div>
          </div>
        </div>
      </SortableContext>
      {deleteConfirmOpened ? (
        <APModal
          open={deleteConfirmOpened}
          className="ap-modal--confirm"
          onClose={(e) => {
            e.stopPropagation();
            setDeleteConfirmOpened(false);
          }}
          size="xs"
          hasHeader={false}
          actions={(
            <>
              <button
                type="button"
                className="button-cancel button"
                onClick={(e) => {
                  e.stopPropagation();
                  setDeleteConfirmOpened(false);
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="button-delete button"
                onClick={deleteGroup}
              >
                Delete
              </button>
            </>
          )}
        >
          Deleting this folder will permanently remove it from your note templates section.
        </APModal>
      ) : null}
    </>
  );
};

export default NoteTemplateGroup;
